import { Component, OnInit, ViewChild } from '@angular/core';
import { mergeMap } from 'rxjs/operators';

import { FormatterHelper } from '../core/helpers';
import { ICESCell, Factory, Catch, Release, Recapture, SelectItem, Filter, FeatureData, FeatureTypes, LayerTypes } from '../core/models';
import { LoggerService, FishMapService, AppSettings } from '../services';
import { ActivatedRoute } from '@angular/router';
import { AppConfig } from '@app/core/models/config/appConfig';
import { FilterRecapture } from '@app/core/models/common/filter-recapture';
import { MatOption } from '@angular/material/core';
import { sortAlphaNum } from '@app/core/helpers/common/utils';
import { PanelOption } from './panel-option';

declare var ol;
@Component({
  selector: 'app-fish-map',
  templateUrl: './fish-map.component.html',
  styleUrls: ['./fish-map.component.scss']
})
export class FishMapComponent implements OnInit {
  @ViewChild('map', { static: true }) map;
  @ViewChild('selectAllExpedition') private selectAllExpedition: MatOption;
  @ViewChild('selectAllFactory') private selectAllFactory: MatOption;
  @ViewChild('selectAllFactoryOnCatches') private selectAllFactoryOnCatches: MatOption;
  @ViewChild('selectAllFactoryOnPanel') private selectAllFactoryOnPanel: MatOption;

  panelOption = PanelOption

  // Options
  panelRecaptures: boolean = false;
  filterMobile: boolean = false;
  showFactories: boolean;
  showICESGrid: boolean;

  activeLayer: LayerTypes;
  activeFishType: string;
  factoriesList: SelectItem[];
  releaseYearsList: SelectItem[];
  catchYearsList: SelectItem[];
  recaptureYearsList: SelectItem[];
  recaptureReleaseYearsList: SelectItem[];
  expeditionList: SelectItem[];

  factories: Factory[];
  catches: Catch[];
  releases: Release[];
  recaptures: Recapture[];
  lastRecaptures: Recapture[];
  icesCells: ICESCell[];
  fishTypes = [];

  // Selected values
  selectedReleaseYear: number;
  selectedCatchYear: number;
  selectedRecaptureYear: number;
  selectedRecaptureFactory: string[];
  selectedCatchFactory: string[];
  selectedFactory: any;
  selectedFeature: any;
  selectedRecaptureListYear: number;
  selectedRecaptureListFactory: string[];
  selectedRecaptureReleaseYear: number;
  selectedRecaptureExpedition: string[];

  // Filters
  releasesFilter: Filter;
  recapturesFilter: Filter;
  catchesFilter: Filter;
  recaptureListFilter: FilterRecapture;

  // Total
  totalReleases: number = 0;
  totalRecaptures: number = 0;
  totalCatches: number = 0;

  formatter: FormatterHelper;

  showDetailsPanel: boolean;
  showLastRecapturesPanel: boolean;
  loaded: boolean;

  constructor(
    private logger: LoggerService,
    private fishMap: FishMapService,
    private appSettingService: AppSettings,
    private route: ActivatedRoute
  ) {
    this.formatter = new FormatterHelper();
    this.showFactories = true;
    this.showICESGrid = true;
    this.showDetailsPanel = false;
    this.showLastRecapturesPanel = true;
    this.activeLayer = LayerTypes.Releases;
    this.factoriesList = [];
  }

  ngOnInit() {

    this.route.data
      .subscribe((data: { appConfig: AppConfig }) => {
        this.mapUrls(data);
      });

    this.resetFilters();

    this.setFilters();

    this.getFishTypes();

  }

  loadData(): void {

    this.loaded = true;
    this.fishMap.getAllData(this.activeFishType).subscribe(data => {

      this.mapData(data)
      this.fillDropdownLists();
      this.setFilters();

    }, error => {

      this.loaded = false;
      this.logger.showErrorMessage("Error while loading data");
    }, () => {
      this.loaded = false;
    });
  }


  getFishTypes() {
    this.loaded = true;
    this.fishMap.getFishTypes().pipe(
      mergeMap(response => {

        this.fishTypes = response;
        this.activeFishType = response[0];
        return this.fishMap.getAllData(this.activeFishType)
      }))
      .subscribe(data => {

        this.mapData(data)
        this.fillDropdownLists();
        this.setFilters();

      }, error => {

        this.loaded = false;
        this.logger.showErrorMessage("Error while loading data");
      }, () => {
        this.loaded = false;
      });
  }

  changeFishType() {
    this.showDetailsPanel = false;
    this.map.removeHighlight();
    this.resetFilters();
    this.loadData();
  }

  fillDropdownLists(): void {

    // Fill factories
    this.factoriesList = this.factoriesList.concat(this.factories.map(f => new SelectItem(f.Id, f.Name)));

    // Fill expeditions
    let allExpedition = this.recaptures.map(f => f.RecapturedExpedition);
    let expeditions = this.formatter.getUniqueItems<string>(allExpedition).sort(sortAlphaNum);
    this.expeditionList = this.expeditionList.concat(expeditions.map(f => new SelectItem(f, f)));

    // Fill release years
    let allYears = this.releases.map(r => r.Year);
    let years = this.formatter.getUniqueItems<number>(allYears);
    this.releaseYearsList = this.releaseYearsList.concat(this.formatter.convertToSelectList(years));

    // Fill recapture years
    allYears = this.recaptures.map(r => r.RecapturedYear);
    years = this.formatter.getUniqueItems<number>(allYears);
    this.recaptureYearsList = this.recaptureYearsList.concat(this.formatter.convertToSelectList(years));

    // Fill catch years
    allYears = this.catches.map(c => c.CatchYear);
    years = this.formatter.getUniqueItems<number>(allYears);
    this.catchYearsList = this.catchYearsList.concat(this.formatter.convertToSelectList(years));

    // Fill recapture release years
    allYears = this.recaptures.map(r => r.ReleasedYear);
    years = this.formatter.getUniqueItems<number>(allYears).sort((a, b) => b - a);
    this.recaptureReleaseYearsList = this.recaptureReleaseYearsList.concat(this.formatter.convertToSelectList(years));
  }

  // setLastRecaptures(): void {
  //   if (this.recaptures) {
  //     let lastDate = new Date();
  //     lastDate.setDate(lastDate.getDate() - 365);
  //     let latest = this.recaptures.filter(r => r.RecapturedTimestamp >= lastDate);
  //     this.lastRecaptures = latest;
  //   }
  // }

  setICESGridVisibility(value: boolean): void {
    this.showICESGrid = value;
  }

  setFactoryVisibility(value: boolean): void {
    this.showFactories = value;
  }

  setReleasesFilter(): void {
    this.resetSelections();
    this.releasesFilter = new Filter(this.selectedReleaseYear);
  }

  setRecapturesFilter(): void {
    this.resetSelections();
    this.recapturesFilter = new FilterRecapture(this.selectedRecaptureYear, this.selectedRecaptureFactory, this.selectedRecaptureReleaseYear, this.selectedRecaptureExpedition);
  }

  setRecaptureListFilter(): void {
    this.resetSelections();
    this.recaptureListFilter = new FilterRecapture(this.selectedRecaptureListYear, this.selectedRecaptureListFactory, 0, []);
  }

  setCatchesFilter(): void {
    this.resetSelections();
    this.catchesFilter = new Filter(this.selectedCatchYear, this.selectedCatchFactory);
  }

  onSelectFeature(data: FeatureData): void {
    switch (data.FeatureType) {
      case FeatureTypes.Releases:
      case FeatureTypes.Recaptures:
      case FeatureTypes.Catches:
        this.selectedFeature = data.Data;
        break;
    }
    if (!this.showDetailsPanel) {
      this.toggleDetails(true);
    }
  }

  resetSelections(): void {
    this.selectedFeature = null;
    this.toggleDetails(false);
  }

  toggleLastRecaptures(value: boolean): void {
    this.showLastRecapturesPanel = value;
    this.updateMapSize();
  }

  toggleDetails(value: boolean): void {
    this.showDetailsPanel = value;
    // this.updateMapSize();
    if (!value && this.map) {
      this.map.removeHighlight();
    }
  }

  updateMapSize(): void {
    if (this.map) {
      this.map.updateMapSize();
    }
  }

  onTotalChange(data: FeatureData): void {
    switch (data.FeatureType) {
      case FeatureTypes.Releases:
        setTimeout(() => {
          this.totalReleases = Math.round(data.Data);
        });
        break;
      case FeatureTypes.Recaptures:
        setTimeout(() => {
          this.totalRecaptures = Math.round(data.Data);
        });
        break;
      case FeatureTypes.Catches:
        setTimeout(() => {
          this.totalCatches = Math.round(data.Data);
        });
        break;
    }
  }

  onClickAllExpedition() {
    if (this.selectAllExpedition.selected) {
      this.selectedRecaptureExpedition = ([...this.expeditionList.map(item => item.Value), '*']);
    } else {
      this.selectedRecaptureExpedition = [];
    }
    this.setRecapturesFilter();
  }

  togglePerOptionForExpedition() {
    if (this.selectAllExpedition.selected) {
      this.selectAllExpedition.deselect();
      this.setRecapturesFilter();
      return false;
    }
    if (this.selectedRecaptureExpedition.length == this.expeditionList.length)
      this.selectAllExpedition.select();

    this.setRecapturesFilter();
  }

  onClickAllFactory(panelOption: number) {
    this.getSelectedFactory(panelOption);
    this.setFilterBySelectedOption(panelOption);
  }

  togglePerOptionForFactory(panelOption: number) {
    this.getToggleSelectedFactory(panelOption);
    this.setFilterBySelectedOption(panelOption);
  }

  getSelectedFactory(panelOption: number) {
    switch (panelOption) {
      case PanelOption.recaptures: {
        if (this.selectAllFactory.selected) {
          this.selectedRecaptureFactory = ([...this.factoriesList.map(item => item.Value), '*']);
        } else {
          this.selectedRecaptureFactory = [];
        }
      }
      case PanelOption.catches: {
        if (this.selectAllFactoryOnCatches.selected) {
          this.selectedCatchFactory = ([...this.factoriesList.map(item => item.Value), '*']);
        } else {
          this.selectedCatchFactory = [];
        }
      }
    }
  }

  getToggleSelectedFactory(panelOption: number) {
    switch (panelOption) {
      case PanelOption.recaptures: {
        if (this.selectAllFactory.selected) {
          this.selectAllFactory.deselect();
          this.setFilterBySelectedOption(panelOption);
          return false;
        }
        if (this.selectedRecaptureFactory.length == this.factoriesList.length)
          this.selectAllFactory.select();
      }
      case PanelOption.catches: {
        if (this.selectAllFactoryOnCatches.selected) {
          this.selectAllFactoryOnCatches.deselect();
          this.setFilterBySelectedOption(panelOption);
          return false;
        }
        if (this.selectedCatchFactory.length == this.factoriesList.length)
          this.selectAllFactoryOnCatches.select();
      }
    }
  }

  setFilterBySelectedOption(panelOption: number) {
    switch (panelOption) {
      case PanelOption.recaptures: {
        this.setRecapturesFilter();
        break;
      }
      case PanelOption.catches: {
        this.setCatchesFilter();
        break;
      }
    }
  }

  onClickPanelAllFactory() {
    if (this.selectAllFactoryOnPanel.selected) {
      this.selectedRecaptureListFactory = ([...this.factoriesList.map(item => item.Value), '*']);
    } else {
      this.selectedRecaptureListFactory = [];
    }
    this.setRecaptureListFilter();
  }

  togglePerOptionForFactoryOnPanel() {
    if (this.selectAllFactoryOnPanel.selected) {
      this.selectAllFactoryOnPanel.deselect();
      this.setRecaptureListFilter();
      return false;
    }
    if (this.selectedRecaptureListFactory.length == this.factoriesList.length)
      this.selectAllFactoryOnPanel.select();

    this.setRecaptureListFilter();
  }

  private resetFilters() {
    this.selectedReleaseYear = 0;

    this.selectedRecaptureYear = 0;
    this.selectedRecaptureFactory = [];

    this.selectedRecaptureListYear = 0;
    this.selectedRecaptureListFactory = [];

    this.selectedCatchYear = 0;
    this.selectedCatchFactory = [];

    this.selectedRecaptureReleaseYear = 0;
    this.selectedRecaptureExpedition = [];

    this.factoriesList = [];
    this.releaseYearsList = [];
    this.recaptureYearsList = [];
    this.catchYearsList = [];
    this.recaptureReleaseYearsList = [];
    this.expeditionList = [];

    // this.factoriesList.push(new SelectItem('', '*'));
    this.releaseYearsList = [new SelectItem(0, '*')];
    this.recaptureYearsList = [new SelectItem(0, '*')];
    this.catchYearsList = [new SelectItem(0, '*')];
    this.recaptureReleaseYearsList = [new SelectItem(0, '*')];
  }

  private setFilters() {
    this.setRecapturesFilter();
    this.setRecaptureListFilter();
    this.setReleasesFilter();
    this.setCatchesFilter();
  }

  private mapData(data: any[]) {
    this.factories = data[0];
    this.releases = data[1];
    this.recaptures = data[2];
    this.catches = data[3];
    this.icesCells = data[4];
  }

  private mapUrls(data: { appConfig: AppConfig }) {
    this.appSettingService.apiBaseUrl = data.appConfig.apiBaseUrl;
    this.appSettingService.apiFishTypeUrl = data.appConfig.apiFishTypeUrl;
    this.appSettingService.apiCatchesUrl = data.appConfig.apiCatchesUrl;
    this.appSettingService.apiFactoriesUrl = data.appConfig.apiFactoriesUrl;
    this.appSettingService.apiReleasesUrl = data.appConfig.apiReleasesUrl;
    this.appSettingService.apiRecapturesUrl = data.appConfig.apiRecapturesUrl;
  }

  showRecapturesList() {
    if (this.panelRecaptures) {
      this.panelRecaptures = false;
    }
    else {
      this.panelRecaptures = true;
    }
  }
  hideFilterMobile() {
    if (this.filterMobile) {
      this.filterMobile = false;
    }
    else {
      this.filterMobile = true;
    }
  }

  trackByFn(index: any, item: any) {
    return index;
  }
}
